import { useCallback, useRef, useState } from "react";
import {
  DEFAULT_ROTATION,
  DEFAULT_TEXT_CONFIGURATION,
} from "../../canvas/config";
import {
  ItemType,
  TextConfiguration,
  TextItem,
  TrackType,
} from "../../../types";
import { getMediaTypeFromFilename } from "../../../../../../utils/getMediaTypeFromFilename";
import {
  generateCaptionDetails,
  generateDynamicFontSize,
} from "../utils/video-preview";
import { useTimelineContext } from "../../remotion-timeline/context/use-timeline-context";
import { calculateTextHeight } from "../utils/measurement";

type UseTimelineSetupProps = {
  video: any;
};

export const useTimelineSetup = ({ video }: UseTimelineSetupProps) => {
  const { setTracks, addTrack, addItem } = useTimelineContext();

  const [isEditorLoaded, setIsEditorLoaded] = useState(false);
  const isInitialMount = useRef(true);
  const currentVideoId = useRef(video.id);

  const clearAllTracks = useCallback(() => {
    setTracks([]);
  }, [setTracks]);

  const configureNewTimeline = useCallback(
    (durationInFrames: number, fps: number) => {
      const videoWidth = video.details?.width || 1280;
      const videoHeight = video.details?.height || 720;

      const captionDetails = generateCaptionDetails(
        video,
        videoWidth,
        videoHeight
      );

      setTracks([]);

      addTrack({
        items: [],
        id: "2",
        name: "Spor 2",
      });

      addTrack({
        items: [],
        id: "1",
        name: "Spor 1",
      });

      if (getMediaTypeFromFilename(video.filename) === "video") {
        addItem(1, {
          id: video.id || "video-main",
          durationInFrames: durationInFrames,
          from: 0,
          isDragging: false,
          type: "video",
          src: video.sourceUrl,
          videoDurationInSeconds: video.duration,
          videoStartFromInSeconds: 0,
          position: {
            x: 0,
            y: 0,
          },
          rotation: {
            degrees: 0,
          },
          configuration: {
            type: "video",
            filters: [],
          },
        });
      }

      if (getMediaTypeFromFilename(video.filename) === "audio") {
        addItem(1, {
          id: video.id || "audio-main",
          durationInFrames: durationInFrames,
          from: 0,
          type: "audio",
          src: video.sourceUrl,
          audioDurationInSeconds: video.duration,
          audioStartFromInSeconds: 0,
        });
      }

      video.content.forEach((contentItem: any, index: number) => {
        const from = Math.round(contentItem.start * fps);
        const durationInSeconds = contentItem.end - contentItem.start;
        let durationInFrames = Math.round(durationInSeconds * fps);

        if (durationInFrames <= 0) {
          durationInFrames = 1;
        }

        const dynamicFontSize = generateDynamicFontSize(
          videoWidth,
          videoHeight,
          captionDetails.size.width
        );

        const fontForMeasurement: TextConfiguration["font"] = {
          ...DEFAULT_TEXT_CONFIGURATION.font,
          size: dynamicFontSize,
        };

        const calculatedHeight = calculateTextHeight(
          contentItem.text,
          captionDetails.size.width,
          fontForMeasurement
        );

        const finalSize = {
          width: captionDetails.size.width,
          height: calculatedHeight + 10,
        };

        const newTextItem: TextItem = {
          id: contentItem.key || `text-${index}`,
          from,
          durationInFrames,
          isDragging: false,
          type: "text",
          text: contentItem.text,
          position: captionDetails.position,
          rotation: DEFAULT_ROTATION,
          size: finalSize,
          configuration: {
            ...DEFAULT_TEXT_CONFIGURATION,
            font: {
              ...DEFAULT_TEXT_CONFIGURATION.font,
              size: generateDynamicFontSize(
                videoWidth,
                videoHeight,
                captionDetails.size.width
              ),
            },
          },
        };

        addItem(0, newTextItem);
      });
    },
    [addItem, addTrack, setTracks, video]
  );

  const configureRevisionTimeline = useCallback(() => {
    clearAllTracks();

    const tracksToAdd: TrackType[] = [...video.content];

    tracksToAdd.sort((a, b) => {
      return Number(b.id) - Number(a.id);
    });

    tracksToAdd.forEach((track: TrackType) => {
      const updatedTrack = {
        ...track,
        items: track.items.map((item: ItemType) => {
          if (item.type === "video") {
            return {
              ...item,
              src: video.sourceUrl,
            };
          }
          return item;
        }),
      };
      addTrack(updatedTrack);
    });
  }, [addTrack, clearAllTracks, video.content, video.sourceUrl]);

  const loadEditor = useCallback(() => {
    const isVideoChanged = currentVideoId.current !== video.id;

    currentVideoId.current = video.id;

    if (isVideoChanged) {
      setTracks([]);
      isInitialMount.current = true;
    }

    if (!video?.sourceUrl || !video?.duration) {
      return;
    }

    const fps = video?.details?.fps || 30;
    const durationInFrames =
      video?.configuration?.durationInFrames ||
      Math.round(video.duration * fps);

    if (video?.version === "v2") {
      configureRevisionTimeline();
    } else {
      configureNewTimeline(durationInFrames, fps);
    }

    setIsEditorLoaded(true);
  }, [
    video?.id,
    video?.sourceUrl,
    video?.duration,
    video?.details?.fps,
    video?.configuration?.durationInFrames,
    video?.version,
    configureNewTimeline,
    configureRevisionTimeline,
    setTracks,
  ]);

  return {
    isEditorLoaded,
    loadEditor,
  };
};
